import React, { useState, useCallback } from 'react';
import { Card } from '@repo/ui/Card';
import { MfaMethodCard } from './MfaMethodCard';
import { MfaAuthenticatorSetup } from './MfaAuthenticatorSetup';
import { MfaEmailSetup } from './MfaEmailSetup';
import { MfaSmsSetup } from './MfaSmsSetup';
import { MfaDisableDialog } from './MfaDisableDialog';
import { BackupTokensCard } from './BackupTokensCard';
import { MfaMethodType } from '../graphql/mfa';

export interface UserAuthProfile {
  id: string;
  mfaTotpEnabled?: boolean;
  mfaEmailEnabled?: boolean;
  mfaSmsEnabled?: boolean;
  mfaSmsPhoneNumber?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPGroupComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPSlotComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QRCodeComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TextInputComponentType = React.ComponentType<any>;

export interface MfaSettingsProps {
  authProfile?: UserAuthProfile;
  userEmail: string;
  onRefetch: () => void;
  DialogComponent: DialogComponentType;
  ButtonComponent: ButtonComponentType;
  InputOTPComponent: InputOTPComponentType;
  InputOTPGroupComponent: InputOTPGroupComponentType;
  InputOTPSlotComponent: InputOTPSlotComponentType;
  QRCodeComponent: QRCodeComponentType;
  TextInputComponent: TextInputComponentType;
  hiddenMethods?: MfaMethodType[];
}

type SetupDialog = MfaMethodType | null;
type DisableDialog = MfaMethodType | null;

export function MfaSettings({
  authProfile,
  userEmail,
  onRefetch,
  DialogComponent,
  ButtonComponent,
  InputOTPComponent,
  InputOTPGroupComponent,
  InputOTPSlotComponent,
  QRCodeComponent,
  TextInputComponent,
  hiddenMethods = [],
}: MfaSettingsProps) {
  const [setupDialog, setSetupDialog] = useState<SetupDialog>(null);
  const [disableDialog, setDisableDialog] = useState<DisableDialog>(null);

  const authenticatorEnabled = authProfile?.mfaTotpEnabled ?? false;
  const emailEnabled = authProfile?.mfaEmailEnabled ?? false;
  const smsEnabled = authProfile?.mfaSmsEnabled ?? false;
  const smsPhoneNumber = authProfile?.mfaSmsPhoneNumber;

  const hasAnyMfaEnabled = authenticatorEnabled || emailEnabled || smsEnabled;

  // Helper to determine if a method should be visible
  // Show if not hidden OR if hidden but already enabled (for disable-only mode)
  const shouldShowMethod = (method: MfaMethodType, isEnabled: boolean): boolean => {
    const isHidden = hiddenMethods.includes(method);
    return !isHidden || isEnabled;
  };

  // Helper to determine if a method can be enabled
  const canEnableMethod = (method: MfaMethodType): boolean => {
    return !hiddenMethods.includes(method);
  };

  const handleSetupSuccess = useCallback(() => {
    setSetupDialog(null);
    onRefetch();
  }, [onRefetch]);

  const handleDisableSuccess = useCallback(() => {
    setDisableDialog(null);
    onRefetch();
  }, [onRefetch]);

  return (
    <Card
      title="Multi-Factor Authentication"
      description="Add additional security to your account by requiring a verification code when you sign in."
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 max-w-lg md:grid-cols-2">
          <MfaMethodCard
            type="authenticator"
            enabled={authenticatorEnabled}
            onEnable={() => setSetupDialog('authenticator')}
            onDisable={() => setDisableDialog('authenticator')}
            ButtonComponent={ButtonComponent}
          />
          <MfaMethodCard
            type="email"
            enabled={emailEnabled}
            onEnable={() => setSetupDialog('email')}
            onDisable={() => setDisableDialog('email')}
            ButtonComponent={ButtonComponent}
          />
          {shouldShowMethod('sms', smsEnabled) && (
            <MfaMethodCard
              type="sms"
              enabled={smsEnabled}
              phoneNumber={smsPhoneNumber}
              onEnable={() => setSetupDialog('sms')}
              onDisable={() => setDisableDialog('sms')}
              ButtonComponent={ButtonComponent}
              hideEnableButton={!canEnableMethod('sms')}
            />
          )}
          <BackupTokensCard
            hasAnyMfaEnabled={hasAnyMfaEnabled}
            onRefetch={onRefetch}
            ButtonComponent={ButtonComponent}
            DialogComponent={DialogComponent}
          />
        </div>

        {setupDialog === 'authenticator' && (
          <MfaAuthenticatorSetup
            userEmail={userEmail}
            onSuccess={handleSetupSuccess}
            onCancel={() => setSetupDialog(null)}
            DialogComponent={DialogComponent}
            ButtonComponent={ButtonComponent}
            InputOTPComponent={InputOTPComponent}
            InputOTPGroupComponent={InputOTPGroupComponent}
            InputOTPSlotComponent={InputOTPSlotComponent}
            QRCodeComponent={QRCodeComponent}
          />
        )}

        {setupDialog === 'email' && (
          <MfaEmailSetup
            userEmail={userEmail}
            onSuccess={handleSetupSuccess}
            onCancel={() => setSetupDialog(null)}
            DialogComponent={DialogComponent}
            ButtonComponent={ButtonComponent}
            InputOTPComponent={InputOTPComponent}
            InputOTPGroupComponent={InputOTPGroupComponent}
            InputOTPSlotComponent={InputOTPSlotComponent}
          />
        )}

        {setupDialog === 'sms' && canEnableMethod('sms') && (
          <MfaSmsSetup
            onSuccess={handleSetupSuccess}
            onCancel={() => setSetupDialog(null)}
            DialogComponent={DialogComponent}
            ButtonComponent={ButtonComponent}
            InputOTPComponent={InputOTPComponent}
            InputOTPGroupComponent={InputOTPGroupComponent}
            InputOTPSlotComponent={InputOTPSlotComponent}
            TextInputComponent={TextInputComponent}
          />
        )}

        {disableDialog && (
          <MfaDisableDialog
            methodToDisable={disableDialog}
            onSuccess={handleDisableSuccess}
            onCancel={() => setDisableDialog(null)}
            DialogComponent={DialogComponent}
            ButtonComponent={ButtonComponent}
            InputOTPComponent={InputOTPComponent}
            InputOTPGroupComponent={InputOTPGroupComponent}
            InputOTPSlotComponent={InputOTPSlotComponent}
          />
        )}
      </div>
    </Card>
  );
}
